import { apiClient } from './client'
import { ApiError } from './errors'

// Matches BankAccountOut from the backend (snake_case)
export interface BankAccount {
  id: number
  account_number: string
  account_name: string
  active: boolean
  verified: boolean
  status: string
  // Whether a proof-of-bank-account document is attached (AML evidence).
  // Present on GET /bank_accounts only — the create response omits it.
  has_proof?: boolean
  // Why the account was rejected, so the customer knows what to correct.
  // Rails exposed this as the GraphQL `comment` field; the REST port dropped
  // it, leaving BankAccountCard's "rejected for the following reason" copy
  // permanently unreachable.
  comment?: string | null
}

// Presigned upload slot returned by POST /bank_accounts/proof_upload — the
// serverless replacement for Rails' /rails/active_storage/direct_uploads.
export interface BankAccountProofUploadSlot {
  /** Server-minted ActiveStorage object key. Send back as proof_key on create. */
  key: string
  /** Presigned S3 PUT url; short-lived (expires_in seconds). */
  url: string
  /** The exact headers the PUT must carry — they are part of the signature. */
  headers: Record<string, string>
  expires_in: number
  /** Filename the backend sanitised and will store on the blob row. */
  filename: string
}

// Mirrors the `blob` params Rails' direct-upload endpoint took, so the client
// prepares the file the same way it always has.
export interface BankAccountProofUploadRequest {
  filename: string
  content_type: string
  byte_size: number
  checksum: string
}

// Question formats served by the backend (identity.py QUESTION_FORMATS)
export type AmlQuestionFormat = 'free_text' | 'single_select' | 'multi_select'

// Matches the answer_options entries serialized by get_aml_questions —
// only the canonical option text and the comments flag cross the wire
export interface AmlAnswerOption {
  answer: string
  comments_required?: boolean
}

// Matches AmlQuestion schema from the backend. `format`/`answer_options` are
// optional so an older backend that serves only key/label/prior_answer still
// renders (the form falls back to a free-text field when they're absent).
export interface AmlQuestion {
  key: string
  label: string
  prior_answer?: string | null
  format?: AmlQuestionFormat | string
  answer_options?: AmlAnswerOption[]
}

// Matches AmlQuestionsOut from the backend
export interface AmlQuestionsResponse {
  questions: AmlQuestion[]
  all_answered: boolean
}

// Single AML answer for submission. `answer` carries the canonical option
// text (multi-select joined with ', ' — the backend matcher splits on
// comma/semicolon); `comments` is required when a chosen option has
// comments_required (e.g. source_of_funds 'Other').
export interface AmlAnswerIn {
  key: string
  answer: string
  comments?: string
}

// Matches IdentityVerificationStatusOut from the backend
export interface IdentityVerificationStatus {
  status: string
  verified?: boolean
  partial_verified?: boolean
  failure_reason?: string
}

// Matches IdentityVerificationRequestIn from the backend
export interface IdentityVerificationRequest {
  first_name: string
  last_name: string
  date_of_birth: string
  address: string
  city: string
  postcode: string
  country_code?: string
}

// Matches CustomerPopupOut from the backend
export interface CustomerPopup {
  id: number
  title: string
  body_text: string
  button_text?: string
  button_link?: string
}

export async function fetchBankAccounts(): Promise<BankAccount[]> {
  try {
    const response = await apiClient.get<BankAccount[]>('bank_accounts')
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function requestBankAccountProofUpload(
  data: BankAccountProofUploadRequest
): Promise<BankAccountProofUploadSlot> {
  try {
    const response = await apiClient.post<BankAccountProofUploadSlot>(
      'bank_accounts/proof_upload',
      data
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export interface CreateBankAccountRequest {
  account_number: string
  account_name: string
  // AML evidence. `proof_key` is the key from requestBankAccountProofUpload —
  // without it the uploaded document is an orphaned blob with no attachment row
  // and the admin approving the account sees no proof.
  proof_key?: string
  proof_filename?: string
}

export async function createBankAccount(
  data: CreateBankAccountRequest
): Promise<BankAccount> {
  try {
    const response = await apiClient.post<BankAccount>('bank_accounts', data)
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function activateBankAccount(id: number): Promise<BankAccount> {
  try {
    const response = await apiClient.put<BankAccount>(
      `bank_accounts/${id}/activate`
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function archiveBankAccount(id: number): Promise<void> {
  try {
    await apiClient.delete(`bank_accounts/${id}`)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchAmlQuestions(): Promise<AmlQuestionsResponse> {
  try {
    const response = await apiClient.get<AmlQuestionsResponse>(
      'compliance/aml_questions'
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function submitAmlAnswers(answers: AmlAnswerIn[]): Promise<void> {
  try {
    await apiClient.post('compliance/aml_answers', { answers })
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchIdentityVerificationStatus(): Promise<IdentityVerificationStatus> {
  try {
    const response = await apiClient.get<IdentityVerificationStatus>(
      'compliance/identity_verification'
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function requestIdentityVerification(
  data: IdentityVerificationRequest
): Promise<IdentityVerificationStatus> {
  try {
    const response = await apiClient.post<IdentityVerificationStatus>(
      'compliance/identity_verification',
      data
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

// Didit hosted IDV (document + biometric/liveness), the live KYC replacing Cloudcheck.
// Returns the hosted URL to redirect the user to; the decision arrives async via the
// backend /webhooks/didit handler, so the app polls fetchIdentityVerificationStatus
// (on the /verifying page) afterwards.
export interface DiditSessionResponse {
  url: string
  session_id: string
  status: string
}

export async function startDiditVerification(
  callback?: string
): Promise<DiditSessionResponse> {
  try {
    const response = await apiClient.post<DiditSessionResponse>(
      'compliance/identity_verification/didit',
      { callback }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchCustomerPopup(): Promise<CustomerPopup | null> {
  try {
    const response = await apiClient.get<CustomerPopup | null>('customer_popup')
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function recordPopupInteraction(popupId: number): Promise<void> {
  try {
    await apiClient.post('customer_popup_interactions', {
      customer_popup_id: popupId
    })
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
